import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { ExternalLink } from '@augmentt-dev/ui-components'
import DaysActiveWidget from '../../../../../widgets/DaysActiveWidget'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import { SHADOW_AI_LOOKBACK_DAYS } from '../../data/constants'
import styles from './ShadowAi.module.scss'

const WEEKDAY_BY_DAY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

// Adapt daily activity to the calendar-week shape expected by DaysActiveWidget.
const buildDaysActiveData = (dailyActivity = [], totalUsers = 0) => {
  const daysActive = []
  let currentWeek = null

  dailyActivity.forEach(({ date, users }) => {
    const weekday = WEEKDAY_BY_DAY_INDEX[new Date(`${date}T00:00:00`).getDay()]

    if (weekday === 'monday' || !currentWeek) {
      currentWeek = {}
      daysActive.push(currentWeek)
    }

    currentWeek[weekday] = { users: users ?? 0, date }
  })

  return { totalUsers, daysActive }
}

export function DaysActiveCell({ dailyActivity = [], totalUsers = 0 }) {
  const recentActivity = dailyActivity.slice(-SHADOW_AI_LOOKBACK_DAYS)

  if (!recentActivity.length) {
    return <span className="text-muted">—</span>
  }

  return (
    <div className={styles.daysActiveCell}>
      <DaysActiveWidget data={buildDaysActiveData(recentActivity, totalUsers)} />
    </div>
  )
}

DaysActiveCell.propTypes = {
  dailyActivity: PropTypes.arrayOf(PropTypes.shape({ date: PropTypes.string, users: PropTypes.number })),
  totalUsers: PropTypes.number,
}

const getSupportPresentation = (supported) => {
  if (supported === true) {
    return { className: styles.supportOk, iconClassName: 'fas fa-check-circle', isUnknown: false }
  }
  if (supported === false) {
    return { className: styles.supportOff, iconClassName: 'fas fa-times-circle', isUnknown: false }
  }
  return { className: styles.supportUnknown, iconClassName: 'fas fa-question-circle', isUnknown: true }
}

function SupportIndicator({ labelId, supported = null }) {
  const presentation = getSupportPresentation(supported)

  return (
    <span className={`${styles.supportItem} ${presentation.className}`}>
      <i className={presentation.iconClassName} aria-hidden="true" />
      <FormattedMessage id={labelId} />
      {presentation.isUnknown && (
        <span>
          (<FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI.SECURITY_UNKNOWN" />)
        </span>
      )}
    </span>
  )
}

SupportIndicator.propTypes = {
  labelId: PropTypes.string.isRequired,
  supported: PropTypes.bool,
}

export function SecurityCell({ ssoSupport = null, mfaSupport = null }) {
  return (
    <div className={styles.securityCell}>
      <SupportIndicator labelId="AI_RISK_REPORT.THIRD.SHADOW_AI.SSO" supported={ssoSupport} />
      <SupportIndicator labelId="AI_RISK_REPORT.THIRD.SHADOW_AI.MFA" supported={mfaSupport} />
    </div>
  )
}

SecurityCell.propTypes = {
  ssoSupport: PropTypes.bool,
  mfaSupport: PropTypes.bool,
}

// Mirrors Discover's "Latest breach" column: the breach year links out to the breach details.
// In the PDF export the year is plain text (no outbound link).
export function BreachCell({ latestBreach = null, breachUrl = null }) {
  const isExportMode = useAIRiskReportExportMode()

  if (!latestBreach) {
    return <span className="text-muted">—</span>
  }

  const year = String(latestBreach).slice(0, 4)

  if (isExportMode || !breachUrl) {
    return <span className="text-body">{year}</span>
  }

  return <ExternalLink href={breachUrl} label={year} />
}

BreachCell.propTypes = {
  latestBreach: PropTypes.string,
  breachUrl: PropTypes.string,
}
